import type { CameraApiResponse } from "@src/services/api/cameras-api";
import { AssetId, CameraId, AssetCameraKey } from "@visioni/a3s-schema";

export function getCameraKeyString(camera: CameraApiResponse): string {
  const key = camera.key;
  try {
    const asset = AssetId.fromString(key.asset);
    const cameraId = CameraId.fromString(key.id);
    const cameraKey = AssetCameraKey.fromParts(asset, cameraId);
    return AssetCameraKey.toString(cameraKey);
  } catch {
    return `${key.asset}@${key.id}`;
  }
}

export function getSiteString(camera: CameraApiResponse): string {
  return camera.site.id;
}

export function getCameraDisplayName(cameraKey: string): string {
  if (!cameraKey) return "";

  try {
    const key = AssetCameraKey.fromString(cameraKey);
    return CameraId.toString(AssetCameraKey.camera(key));
  } catch {
    return cameraKey;
  }
}

export function isLegacyAppCameraKey(cameraKey: string): boolean {
  try {
    const key = AssetCameraKey.fromString(cameraKey);
    const asset = AssetCameraKey.asset(key);
    const source = AssetId.source(asset);
    return source === "legacy_app";
  } catch {
    return false;
  }
}

export function isLegacyAppCamera(camera: CameraApiResponse): boolean {
  try {
    const asset = AssetId.fromString(camera.key.asset);
    const source = AssetId.source(asset);
    return source === "legacy_app";
  } catch {
    return false;
  }
}

/**
 * Extracts the camera source and generation (fleet) from the camera's asset ID.
 * Returns an object with source and fleet, or undefined if extraction fails.
 * Relies on AssetId.toParts() - if that fails, returns undefined.
 */
export function getCameraSourceAndFleet(camera: CameraApiResponse): { source: string; fleet: string } | undefined {
  try {
    const asset = AssetId.fromString(camera.key.asset);
    const parts = AssetId.toParts(asset);
    return {
      source: parts.source,
      fleet: parts.fleet,
    };
  } catch {
    return undefined;
  }
}
